import { TFunction } from 'i18next'

import { PriceOffer, Product, ProductPriceOffer } from '@shared/generated'
import { formatGramsToHumanWeight } from '@shared/uttils/formatGramsToHumanWeight'

export type MaxAmountResult = {
  maxAmount: number;
  source: 'max_amount' | 'stock' | 'default';
};

export type ValidationResult = {
  isValid: boolean;
  normalized: number;
  min: number;
  max: number;
  step: number;
};

export const getMinAmount = (product: Product): number => {
  if (!product?.priceOffer) return 1
  const amountInQuant = getAmountInBaseQuant(product.priceOffer as unknown as ProductPriceOffer)
  const minFromOffer = product.priceOffer.min as number
  return ((minFromOffer > 0 ? minFromOffer : (product.priceOffer.step as number)) * amountInQuant)
}

export const normalizeAmount = (amount: number, step: number): number => {
  if (step <= 0) return amount
  return Math.round(amount / step) * step
}

export const validateAmount = (
  amount: number,
  product: Product,
): ValidationResult => {
  const step = getAmountStep(product)
  const min = getMinAmount(product)
  const { maxAmount } = getMaxAmount(product)

  const normalized = normalizeAmount(amount, step)
  const finalAmount = Math.max(min, Math.min(normalized, maxAmount))

  return {
    isValid: normalized >= min && normalized <= maxAmount,
    normalized: finalAmount,
    min,
    max: maxAmount,
    step,
  }
}

export const getAmountInQuantType = (priceOffer: ProductPriceOffer): number => {
  switch (priceOffer?.uiQuant) {
  case 'KGM':
    return 1000
  default:
    return 1
  }
}

export const getAmountInBaseQuant = (priceOffer: ProductPriceOffer): number =>
  (priceOffer?.quantAmount ?? 1) * getAmountInQuantType(priceOffer)

type FormatAmountWithUnitMultiplierArgs = {
  product: Product;
  unit_multiplier: number;
  t: TFunction;
}

export const formatAmountWithUnitMultiplier = ({
  product,
  unit_multiplier,
  t
}: FormatAmountWithUnitMultiplierArgs): string => {
  const isWeight = isWeightProduct(product.price_type as 'weight' | 'piece')

  if (!isWeight) {
    return `${unit_multiplier} ${t('weight.pc')}`
  }

  return formatGramsToHumanWeight({
    weight: unit_multiplier,
    t,
    showDecimal: true,
  })
}

type FormatQuantArgs = {
  quant?: string
  t: TFunction;
}

export const formatQuant = ({
  quant = 'XPC',
  t
}: FormatQuantArgs): string => {
  return t(`units.${quant}`)
}

type FormatPriceAmountArgs = {
  priceOffer: PriceOffer;
  t: TFunction;
}

export const formatPriceAmount = ({
  priceOffer,
  t
}: FormatPriceAmountArgs): string => {
  const quantType = priceOffer?.quant || 'XPC'
  const quantAmount = priceOffer?.quantAmount || 1

  // return `${quantAmount} ${formatQuant({ t,})}`

  return `${quantAmount} ${t(`units.${quantType}`)}`
}

type FormatAmountArgs = {
  product: Product;
  amount: number;
  t: TFunction;
  showOne?: boolean;
};

export const formatAmount = ({
  product,
  amount,
  t,
  showOne = false,
}: FormatAmountArgs): string => {
  if (isWeightProduct(product.price_type as 'weight' | 'piece')) {
    return formatGramsToHumanWeight({
      weight: amount,
      t,
    })
  }
  return `${amount > 1 || showOne ? `${amount} ` : ''}${t('weight.pc')}`
}

export const isWeightProduct = (price_type: 'weight' | 'piece'): boolean =>
  price_type === 'weight'

export const stockAmount = (
  product: Product,
  defaultValue: number = 0,
): number => {
  const amount = product?.stock?.amount ?? 0
  return amount > 0 ? amount : defaultValue
}

export const getMaxAmount = (product: Product): MaxAmountResult => {
  const priceOffer = product.priceOffer as PriceOffer
  const availableStock = stockAmount(product, 0)

  if (
    (priceOffer?.max as number) > 0 &&
    (availableStock === 0 || (priceOffer?.max as number) < availableStock)
  ) {
    return {
      maxAmount: priceOffer?.max as number,
      source: 'max_amount',
    }
  }

  if (availableStock > 0) {
    return {
      maxAmount: availableStock,
      source: 'stock',
    }
  }

  return {
    maxAmount: 99999,
    source: 'default',
  }
}

export const getAmountStep = (product: Product): number => {
  const priceOffer = product.priceOffer as PriceOffer
  const amountInQuant = getAmountInBaseQuant(priceOffer as unknown as ProductPriceOffer)
  return ((priceOffer?.step as number) > 0 ? (priceOffer?.step as number) : 1) * amountInQuant
}

export const isAgeRestrictedProduct = (product: Pick<Product, 'is_alcohol' | 'min_age'>): boolean =>
  Boolean(product.is_alcohol || ((product.min_age ?? 0) > 0))

export const isCartButtonDisabled = (product: Product): boolean => {
  return stockAmount(product, 0) <= 0
}

export const calculateItemTotal = (
  price: number,
  amount: number,
  countMultiplier: number = 1,
): number => {
  return (price * amount) / countMultiplier
}

export const isAmountValid = (amount: number, product: Product): boolean => {
  const min = getMinAmount(product)
  const { maxAmount } = getMaxAmount(product)
  const step = getAmountStep(product)
  const normalized = normalizeAmount(amount, step)

  return normalized >= min && normalized <= maxAmount
}

export const getNextValidAmount = (
  currentAmount: number,
  product: Product,
  direction: 1 | -1 = 1,
): number => {
  const step = getAmountStep(product)
  const newAmount = normalizeAmount(currentAmount + step * direction, step)
  const { min, max } = validateAmount(newAmount, product)

  return Math.min(Math.max(newAmount, min), max)
}
